import { useEffect, useMemo, useRef, useState } from 'react';
import { CONTENT_PUBLIC_URL } from '@dnyaneshwari/shared';
import type { Ovi } from '@dnyaneshwari/shared';
import {
  getChapterOviText,
  saveChapterOviText,
  clearOviTextCorrection,
  getChapterMeanings,
  saveChapterMeanings,
  parseMeaningsText,
} from '@/services/adminService';

const CONTENT_BASE = (import.meta.env.VITE_CONTENT_BASE_URL as string | undefined) ?? CONTENT_PUBLIC_URL;
const key = (n: number) => String(n).padStart(3, '0');
/**
 * Two lines are the same verse only if they read the same, dandas and all.
 * An earlier version compared the letters alone, which meant a correction
 * that moved a danda -- the whole point of some of them -- was taken for no
 * change and thrown away.
 */
const norm = (s: string) => s.normalize('NFC').replace(/\s+/g, ' ').trim();
const same = (a: string, b: string) => norm(a) === norm(b);

type Field = 'verse' | 'meaning';

/**
 * A chapter's ovis and their भावार्थ, side by side.
 *
 * The two were separate tabs once, which made the proofreader open a verse in
 * one and its explanation in the other. She is checking both against the same
 * open page of the book, so they belong on one line: the verse as the app
 * ships it with a box to correct it, and the भावार्थ beside it.
 *
 * Each box saves on its own, a moment after typing stops, into its own
 * document -- the verse into oviText/{chapter}, the भावार्थ into
 * meanings/{chapter}. Both reach every installed app at its next open, with
 * no release. Emptying a verse box puts the shipped text back; the भावार्थ
 * has no shipped text to fall back to, so emptying it clears the paragraph.
 */
export default function ChapterEditPanel({ chapterId }: { chapterId: string }) {
  const [ovis, setOvis] = useState<Ovi[]>([]);
  const [verses, setVerses] = useState<Record<string, string>>({});
  const [meanings, setMeanings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [filter, setFilter] = useState('');
  const [onlyChanged, setOnlyChanged] = useState(false);
  // What is in each box, which is not the same as what is stored: a verse
  // typed back exactly as the app already has it is not a correction, but it
  // must stay on screen. Deleting the stored entry used to empty the box
  // under the typist's hands.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [importFor, setImportFor] = useState<Field | null>(null);
  const [importText, setImportText] = useState('');
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      fetch(`${CONTENT_BASE}/chapters/${chapterId.padStart(2, '0')}.json`).then((r) => (r.ok ? r.json() : { ovis: [] })),
      getChapterOviText(chapterId),
      getChapterMeanings(chapterId),
    ]).then(([ch, v, m]: [{ ovis: Ovi[] }, Record<string, string>, Record<string, string>]) => {
      if (!active) return;
      setOvis(ch.ovis);
      setVerses(v);
      setMeanings(m);
      setLoading(false);
    });
    return () => { active = false; };
  }, [chapterId]);

  /** One box at a time, a moment after typing stops. */
  function edit(field: Field, n: number, text: string) {
    const k = key(n);
    const timerKey = `${field}:${k}`;
    setDrafts((prev) => ({ ...prev, [timerKey]: text }));

    const pending = timers.current.get(timerKey);
    if (pending) clearTimeout(pending);
    timers.current.set(timerKey, setTimeout(async () => {
      timers.current.delete(timerKey);
      const label = field === 'verse' ? 'ओवी' : 'भावार्थ';
      try {
        if (field === 'verse') {
          // The app ships the verse, so an empty box or a copy of the shipped
          // line is not a correction -- it is the correction being taken back.
          const shipped = ovis.find((o) => o.oviNumber === n)?.textMarathi ?? '';
          if (!text.trim() || same(text, shipped)) {
            // Nothing to store -- but the box keeps what was typed.
            await clearOviTextCorrection(chapterId, n);
            setVerses((prev) => { const next = { ...prev }; delete next[k]; return next; });
            setStatus(text.trim() ? `ओवी ${n}: ॲपमधील मजकुराशी जुळते` : `ओवी ${n}: मूळ मजकूर परत`);
          } else {
            await saveChapterOviText(chapterId, { [k]: text.trim() });
            setVerses((prev) => ({ ...prev, [k]: text.trim() }));
            setStatus(`ओवी ${n} जतन केली`);
          }
        } else {
          await saveChapterMeanings(chapterId, { [k]: text.trim() });
          setMeanings((prev) => ({ ...prev, [k]: text.trim() }));
          setStatus(`ओवी ${n} चा भावार्थ जतन केला`);
        }
      } catch (e) {
        setStatus(`ओवी ${n} चा ${label} जतन झाला नाही: ${(e as Error).message}`);
      }
      setTimeout(() => setStatus(''), 2500);
    }, 900));
  }

  async function runImport() {
    if (!importFor) return;
    const parsed = parseMeaningsText(importText);
    if (!Object.keys(parsed).length) { setStatus('एकही ओवी सापडली नाही'); return; }

    let toSave = parsed;
    if (importFor === 'verse') {
      // Anything identical to the shipped verse is not a correction; skipping
      // it keeps the document to the lines that actually changed.
      const shipped = new Map(ovis.map((o) => [key(o.oviNumber), o.textMarathi]));
      toSave = Object.fromEntries(
        Object.entries(parsed).filter(([k, v]) => !same(v, shipped.get(k) ?? ''))
      );
      if (!Object.keys(toSave).length) {
        setStatus(`${Object.keys(parsed).length} ओव्या आधीच जुळतात — बदल नाही`);
        return;
      }
    }

    const count = Object.keys(toSave).length;
    setStatus(`${count} नोंदी जतन होत आहेत…`);
    try {
      if (importFor === 'verse') {
        await saveChapterOviText(chapterId, toSave);
        setVerses((prev) => ({ ...prev, ...toSave }));
      } else {
        await saveChapterMeanings(chapterId, toSave);
        setMeanings((prev) => ({ ...prev, ...toSave }));
      }
      setImportFor(null);
      setImportText('');
      setStatus(`${count} नोंदी जतन झाल्या`);
    } catch (e) {
      setStatus(`जतन झाले नाही: ${(e as Error).message}`);
    }
    setTimeout(() => setStatus(''), 4000);
  }

  const shown = useMemo(() => {
    const q = filter.trim();
    return ovis.filter((o) => {
      const k = key(o.oviNumber);
      if (onlyChanged && !verses[k] && !meanings[k]) return false;
      if (!q) return true;
      if (/^\d+$/.test(q)) return String(o.oviNumber).startsWith(q);
      return o.textMarathi.includes(q) || (verses[k] ?? '').includes(q) || (meanings[k] ?? '').includes(q);
    });
  }, [ovis, verses, meanings, filter, onlyChanged]);

  const verseCount = Object.keys(verses).length;
  const meaningCount = Object.keys(meanings).length;

  if (loading) return <p className="p-4 text-sm text-gray-500">ओव्या उघडत आहेत…</p>;

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="ओवी क्रमांक किंवा शब्द शोधा"
          className="px-3 py-2 border rounded-lg text-sm w-60"
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
          फक्त बदललेल्या
        </label>
        <button
          onClick={() => { setImportFor(importFor === 'verse' ? null : 'verse'); setImportText(''); }}
          className="px-3 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium"
        >
          ओव्या आयात करा
        </button>
        <button
          onClick={() => { setImportFor(importFor === 'meaning' ? null : 'meaning'); setImportText(''); }}
          className="px-3 py-2 rounded-lg bg-amber-600 text-white text-sm font-medium"
        >
          भावार्थ आयात करा
        </button>
        <span className="text-sm text-gray-600">
          {verseCount} ओव्या दुरुस्त · {meaningCount} / {ovis.length} भावार्थ
        </span>
        {status && <span className="text-sm text-green-700">{status}</span>}
      </div>

      {importFor && (
        <div className="p-4 border rounded-xl bg-amber-50 space-y-3">
          <p className="text-sm text-gray-700">
            {importFor === 'verse' ? 'प्रत्येक ओवी' : 'प्रत्येक भावार्थ'} तिच्या क्रमांकाने सुरू करा —{' '}
            <code>85. ...</code> किंवा <code>८५ ...</code>.
            {importFor === 'verse' && ' ॲपमधील मजकुराशी जुळणाऱ्या ओव्या वगळल्या जातात.'}
          </p>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={10}
            className="w-full px-3 py-2 border rounded-lg font-marathi text-sm"
            placeholder={'85. ...\n86. ...'}
          />
          <div className="flex gap-2">
            <button onClick={runImport} className="px-4 py-2 rounded-lg bg-green-600 text-white text-sm font-medium">
              तपासून जतन करा
            </button>
            <button onClick={() => setImportFor(null)} className="px-4 py-2 rounded-lg border text-sm">रद्द</button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {shown.map((o) => {
          const k = key(o.oviNumber);
          const verse = verses[k];
          const meaning = meanings[k];
          // The box shows what was typed, falling back to what is stored.
          const verseBox = drafts[`verse:${k}`] ?? verse ?? '';
          const meaningBox = drafts[`meaning:${k}`] ?? meaning ?? '';
          const matchesShipped = !verse && verseBox.trim().length > 0;
          return (
            <div
              key={o.oviNumber}
              className={`p-3 rounded-xl border ${verse || meaning ? 'border-green-300 bg-green-50/30' : 'border-gray-200'}`}
            >
              <div className="flex items-start gap-3">
                <span className="mt-1 w-9 shrink-0 text-center text-xs font-semibold text-orange-700">
                  {o.oviNumber}
                </span>
                <div className="flex-1 grid gap-4 md:grid-cols-2">
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-600">ओवी</p>
                    <p className="text-sm text-gray-700 whitespace-pre-line font-marathi">{o.textMarathi}</p>
                    <textarea
                      value={verseBox}
                      onChange={(e) => edit('verse', o.oviNumber, e.target.value)}
                      rows={2}
                      placeholder="पुस्तकाप्रमाणे ओवी इथे लिहा"
                      className="w-full px-3 py-2 border rounded-lg text-sm font-marathi"
                    />
                    <p className="text-[11px] text-gray-500">
                      {verse
                        ? 'दुरुस्त — हेच ॲपमध्ये दिसते'
                        : matchesShipped
                          ? 'ॲपमधील मजकुराशी जुळते — दुरुस्ती नोंदवली नाही'
                          : 'दुरुस्ती नाही'}
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-gray-600">भावार्थ</p>
                    <textarea
                      value={meaningBox}
                      onChange={(e) => edit('meaning', o.oviNumber, e.target.value)}
                      rows={4}
                      placeholder="या ओवीचा अर्थ सोप्या मराठीत"
                      className="w-full px-3 py-2 border rounded-lg text-sm font-marathi"
                    />
                    <p className="text-[11px] text-gray-500">
                      {meaningBox.trim() ? 'ॲपमध्ये दिसतो' : 'अजून लिहिलेला नाही'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {!shown.length && <p className="text-sm text-gray-500">काही सापडले नाही.</p>}
      </div>
    </div>
  );
}
